import React, { useEffect, useMemo, useState, useRef } from 'react'
import PinchZoomImage from './PinchZoomImage'
import ImageFullscreenModal from './ImageFullscreenModal'
import { placeholderGeneral } from '../Constants'
import styles from './ImageWithZoomStyles'
import { Image as ExpoImage } from 'expo-image'
import { Image as RNImage, View, Text, Linking, Platform, Animated, TouchableOpacity } from 'react-native'
import { Feather } from '@expo/vector-icons'
import { documentDirectory, getInfoAsync } from "../../../utils/fileSystemCompat"
import { isWeb } from '../../../utils/platformUtils'
import { fetchAuthSession } from 'aws-amplify/auth'
import SizeCache from '../../../utils/sizeCache'

const sanitizeUri = (u) => {
    if (!u || typeof u !== 'string') return u
    let s = u.trim()
    if (s.startsWith('//')) s = 'https:' + s
    if (s.toLowerCase().startsWith('http://')) {
        // Auto-upgrade to https to satisfy iOS ATS and avoid mixed content
        s = 'https://' + s.slice('http://'.length)
    }
    if (!/^https?:\/\//i.test(s)) {
        // evitar prefijar rutas locales file://
        if (!s.startsWith('file://')) {
            // debug suprimido: prefix https
            s = 'https://' + s
        }
    }
    return s
}

const isHttpUrl = (u) => typeof u === 'string' && /^https?:\/\//i.test(u)

/**
 * @typedef {Object} ImageWithZoomProps
 * @property {string} uri
 * @property {(v?: any) => void} [contentSeen]
 * @property {() => void} [onReady]
 * @property {string} [path]
 * @property {number} [containerWidth]
 */

/**
 * @param {ImageWithZoomProps} props
 */
// Cache en módulo para ratios calculados por uri (persistencia solo en sesión)
const RATIO_CACHE = new Map()
// Cache simple de imagen resuelta local (webp/original) para evitar repetir searchFile en remount inmediato
const RESOLVED_URI_CACHE = new Map()

export const ImageWithZoom = React.memo(({ 
    uri, 
    contentSeen, 
    path, 
    containerWidth = undefined, 
    onReady, 
    initialRatio = null,
    onDoubleTap,
    onLongPress,
    onZoomChange
}) => {
    const [image, setImage] = useState('')
    const [useLocalFallback, setUseLocalFallback] = useState(false)
    // aspectRatio dinámico basado en el asset; iniciamos en 1 para evitar saltos fuertes
    const initialRatioValue = (() => {
        // 1) Preferir initialRatio (desde servicio)
        if (typeof initialRatio === 'number' && initialRatio > 0.2 && initialRatio < 5) {
            try { if (uri) SizeCache.setRatioForUrl?.(uri, initialRatio) } catch {}
            if (uri) RATIO_CACHE.set(uri, initialRatio)
            return initialRatio
        }
        // 2) Cache de sesión
        if (uri && RATIO_CACHE.has(uri)) return RATIO_CACHE.get(uri) || 1
        // 3) Cache persistente
        try {
            const r = uri ? SizeCache.getRatioForUrl?.(uri) : null
            if (typeof r === 'number' && r > 0.2 && r < 5) return r
        } catch {}
        // 4) Sin datos: usar 1 (cuadrado) como placeholder más neutro que 16:9
        return 1
    })()
    const [ratio, setRatio] = useState(initialRatioValue)
    const lockLayoutRef = useRef(true) // permitir como máximo 1 corrección de ratio en la sesión para evitar pantallasos
    const [authHeaders, setAuthHeaders] = useState(null)
    // Por defecto no enviar headers; si falla y hay token, alternamos a con headers.
    const [skipHeaders, setSkipHeaders] = useState(true)
    
    // Estado para modal de fullscreen (solo web)
    const [showFullscreen, setShowFullscreen] = useState(false)
    const [attemptCount, setAttemptCount] = useState(0)
    // Forzar recarga controlada al fallar para evitar caches
    const [reloadTick, setReloadTick] = useState(0)
    // Fallback para nativo: si ExpoImage no carga, usar RNImage
    const [useRNImageNative, setUseRNImageNative] = useState(false)
    const [startedLoad, setStartedLoad] = useState(false)
    const [notifiedReady, setNotifiedReady] = useState(false)

    const notifyReadyOnce = () => {
        if (!notifiedReady) {
            setNotifiedReady(true)
            try { typeof onReady === 'function' && onReady() } catch {}
        }
    }

    useEffect(() => {
        let mounted = true
        ;(async () => {
            try {
                const session = await fetchAuthSession()
                const raw = session?.tokens?.accessToken?.toString?.()
                const token = raw ? `Bearer ${raw}` : undefined
                if (mounted && token) {
                    setAuthHeaders({ Authorization: token })
                }
            } catch {}
        })()
        return () => {
            mounted = false
        }
    }, [])

    useEffect(() => {
        if (!uri) {
            setImage(placeholderGeneral)
            return
        }
        const sanitized = sanitizeUri(uri)
        // Si ya tenemos resolución cacheada evitar IO
        if (!path || path.trim() === '') {
            setImage(sanitized)
            return
        }
        const cacheKey = path + '::' + sanitized
        const cachedResolved = RESOLVED_URI_CACHE.get(cacheKey)
        if (cachedResolved) {
            setImage(cachedResolved)
            return
        }
        let cancelled = false
        ;(async () => {
            try {
                const imageLocalOrRemote = await searchFile(sanitized, path)
                if (!cancelled) {
                    RESOLVED_URI_CACHE.set(cacheKey, imageLocalOrRemote)
                    setImage(imageLocalOrRemote)
                }
            } catch {
                if (!cancelled) setImage(sanitized)
            }
        })()
        return () => { cancelled = true }
    }, [uri, path])

    // Fade suave del contenedor cuando corregimos el ratio real
    const containerOpacity = useRef(new Animated.Value(1)).current
    // Intenta actualizar el ratio en sesión si hay una desviación significativa.
    // También persiste en caches para futuras monturas.
    const maybeUpdateRatio = (newRatio) => {
        try {
            if (!newRatio || newRatio <= 0.2 || newRatio >= 5) return
            if (uri) {
                RATIO_CACHE.set(uri, newRatio)
                try { SizeCache.setRatioForUrl?.(uri, newRatio) } catch {}
            }
            const prev = ratio || initialRatioValue || 1
            const deviation = Math.abs(newRatio - prev) / (prev || newRatio)
            const shouldAdjust = lockLayoutRef.current && (initialRatioValue === 1 || deviation > 0.12)
            if (!shouldAdjust) return
            // Una única corrección suave en esta sesión
            lockLayoutRef.current = false
            try {
                Animated.timing(containerOpacity, { toValue: 0.85, duration: 120, useNativeDriver: true }).start(() => {
                    setRatio(newRatio)
                    Animated.timing(containerOpacity, { toValue: 1, duration: 180, useNativeDriver: true }).start()
                })
            } catch {
                setRatio(newRatio)
            }
        } catch {}
    }

    // En Web: medir el tamaño natural con un probe HTMLImageElement para calcular el aspectRatio
    useEffect(() => {
        if (!isWeb()) return
        if (!image || useLocalFallback) return
        try {
            const probe = new (globalThis?.Image || window?.Image)()
            // Evitar cache para obtener tamaño correcto si cambia de asset
            const src = image ? (image.includes('?') ? `${image}&probe=${Date.now()}` : `${image}?probe=${Date.now()}`) : image
            probe.onload = () => {
                const w = probe.naturalWidth || probe.width
                const h = probe.naturalHeight || probe.height
                if (w && h && h > 0) {
                    const newRatio = w / h
                    // Persistir y, si corresponde, corregir en sesión con fade
                    maybeUpdateRatio(newRatio)
                    // debug suprimido: probe size
                }
            }
            probe.onerror = () => {
                // Mantener ratio actual y no romper UI
            }
            probe.src = src
            return () => {
                // limpiar handlers por seguridad
                probe.onload = null
                probe.onerror = null
            }
        } catch {}
    }, [image, useLocalFallback])

    // Si ExpoImage no inicia carga en nativo, forzar fallback a RNImage luego de un pequeño timeout
    useEffect(() => {
        if (isWeb()) return
        if (!image) return
        if (useRNImageNative) return
        if (!isHttpUrl(image)) return
        setStartedLoad(false)
        const t = setTimeout(() => {
            if (!startedLoad) {
                // debug suprimido: timeout fallback
                setUseRNImageNative(true)
            }
        }, 1500)
        return () => clearTimeout(t)
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [image, reloadTick])

    const widthPx = typeof containerWidth === 'number' && containerWidth > 0 ? containerWidth : undefined
    const heightPx = widthPx && ratio ? Math.max(1, Math.round(widthPx / ratio)) : undefined

    // Guardar ratio en cache cuando cambia
    useEffect(() => {
        if (uri && ratio && ratio > 0.2 && ratio < 5) {
            RATIO_CACHE.set(uri, ratio)
            try { SizeCache.setRatioForUrl?.(uri, ratio) } catch {}
        }
    }, [uri, ratio])

    // Memoizar sources para evitar cambiar la referencia del objeto entre renders
    const webImageSource = useMemo(() => {
        if (useLocalFallback) return require('../../../../assets/placeholderImage.png')
        const final = image ? (isHttpUrl(image) ? (reloadTick > 0 ? (image.includes('?') ? `${image}&t=${reloadTick}` : `${image}?t=${reloadTick}`) : image) : image) : undefined
        return { uri: final }
    }, [useLocalFallback, image, reloadTick])

    const rnImageNativeSource = useMemo(() => {
        if (useLocalFallback) return require('../../../../assets/placeholderImage.png')
        const baseUri = image ? (reloadTick > 0 && isHttpUrl(image) ? (image.includes('?') ? `${image}&t=${reloadTick}` : `${image}?t=${reloadTick}`) : image) : undefined
        if (skipHeaders || !authHeaders) return { uri: baseUri }
        return { uri: baseUri, headers: authHeaders }
    }, [useLocalFallback, image, reloadTick, skipHeaders, authHeaders])

    const expoImageSource = useMemo(() => {
        if (useLocalFallback) return require('../../../../assets/placeholderImage.png')
        const baseUri = image ? (reloadTick > 0 && isHttpUrl(image) ? (image.includes('?') ? `${image}&t=${reloadTick}` : `${image}?t=${reloadTick}`) : image) : undefined
        if (isWeb() || skipHeaders || !authHeaders) return { uri: baseUri }
        return { uri: baseUri, headers: authHeaders }
    }, [useLocalFallback, image, reloadTick, skipHeaders, authHeaders])

    return (
        <>
            {(image || useLocalFallback) && (
                // En Web no aplicamos gestos de zoom; en mobile sí con PinchZoomImage
                (isWeb() ? (
                    <View style={{ width: '100%', position: 'relative' }}>
                        <Animated.View style={{ width: '100%', minHeight: 160, opacity: containerOpacity }}>
                            <RNImage
                                style={[styles.imageWithZoom, widthPx ? { width: widthPx, height: heightPx } : { aspectRatio: ratio }]}
                                // En web, evitar enviar Authorization en recursos públicos (S3) para no gatillar preflight/CORS 400
                                source={webImageSource}
                                resizeMode='contain'
                                onError={(e) => {
                                    // debug suprimido: onError web RNImage
                                    // Intentar un reintento con cache-busting una sola vez antes del fallback
                                    setReloadTick((x) => (x === 0 ? 1 : x))
                                    if (reloadTick > 0) setUseLocalFallback(true)
                                    // Asegurar que el contenedor quite el overlay
                                    try { typeof onReady === 'function' && onReady() } catch {}
                                }}
                                onLoad={(e) => {
                                    try {
                                        const w = e?.nativeEvent?.source?.width
                                        const h = e?.nativeEvent?.source?.height
                                        if (w && h && h > 0) {
                                            const newRatio = w / h
                                            maybeUpdateRatio(newRatio)
                                        }
                                        notifyReadyOnce()
                                    } catch {}
                                }}
                                onLoadStart={() => {}}
                                onLoadEnd={() => contentSeen && contentSeen()}
                            />
                        </Animated.View>
                        
                        <TouchableOpacity
                            style={{
                                position: 'absolute',
                                top: 12,
                                right: 12,
                                backgroundColor: 'rgba(0,0,0,0.45)',
                                borderRadius: 20,
                                padding: 10,
                                zIndex: 99999,
                                cursor: 'pointer',
                                transition: 'all 0.2s ease',
                            }}
                            onPress={(e) => {
                                e.stopPropagation()
                                setShowFullscreen(true)
                            }}
                            activeOpacity={0.7}
                            hitSlop={{ top: 10, bottom: 10, left: 10, right: 10 }}
                            onMouseEnter={(e) => {
                                e.currentTarget.style.backgroundColor = 'rgba(0, 0, 0, 0.8)'
                                e.currentTarget.style.transform = 'scale(1.1)'
                            }}
                            onMouseLeave={(e) => {
                                e.currentTarget.style.backgroundColor = 'rgba(0, 0, 0, 0.45)'
                                e.currentTarget.style.transform = 'scale(1)'
                            }}
                        >
                            <Feather name="maximize" size={22} color="#fff" />
                        </TouchableOpacity>
                        
                        <ImageFullscreenModal
                            visible={showFullscreen}
                            imageUri={image}
                            onClose={() => setShowFullscreen(false)}
                        />
                    </View>
                ) : (
                    <PinchZoomImage 
                        enabled={true} 
                        onDoubleTap={onDoubleTap}
                        onLongPress={onLongPress}
                        onZoomChange={onZoomChange}
                    >
                        <View style={{ width: '100%', minHeight: 160, overflow: 'visible' }}>
                            {useRNImageNative ? (
                                <RNImage
                                    style={[styles.imageWithZoom, widthPx ? { width: widthPx, height: heightPx } : { aspectRatio: ratio }]}
                                    source={rnImageNativeSource}
                                    resizeMode='contain'
                                    onLoad={(e) => {
                                        try {
                                            const w = e?.nativeEvent?.source?.width
                                            const h = e?.nativeEvent?.source?.height
                                            if (w && h && h > 0) {
                                                const newRatio = w / h
                                                maybeUpdateRatio(newRatio)
                                            }
                                            notifyReadyOnce()
                                        } catch {}
                                    }}
                                    onError={(e) => {
                                        // debug suprimido: RNImage onError
                                        setUseLocalFallback(true)
                                        // Asegurar que el contenedor quite el overlay
                                        try { typeof onReady === 'function' && onReady() } catch {}
                                    }}
                        onLoadStart={() => {}}
                                />
                            ) : (
                            <ExpoImage
                                style={[styles.imageWithZoom, widthPx ? { width: widthPx, height: heightPx } : { aspectRatio: ratio }]}
                                // En web, evitar enviar Authorization (CORS). En nativo, usar headers si están disponibles
                                source={expoImageSource}
                                contentFit='contain'
                                transition={0}
                                cachePolicy={'disk'}
                                onLoad={(e) => {
                                    try {
                                        const info = e?.source || (e?.nativeEvent?.source) || e
                                        const w = info?.width
                                        const h = info?.height
                                        if (w && h && h > 0) {
                                            const newRatio = w / h
                                            maybeUpdateRatio(newRatio)
                                        }
                                        notifyReadyOnce()
                                    } catch {}
                                }}
                                onError={(e) => {
                                    console.log('[ImageWithZoom] onError (native) ->', e?.nativeEvent)
                                    // Estrategia de reintentos (máx 2):
                                    // 1) Si estamos sin headers y tenemos token -> reintentar con headers
                                    // 2) Si estamos con headers -> reintentar sin headers
                                    // 3) Si ya intentamos ambas, usar fallback local
                                    setAttemptCount((n) => n + 1)
                                    if (isWeb()) {
                                        // En web no usamos headers; solo fallback si ya reintentamos una vez
                                        setReloadTick((x) => x + 1)
                                        setUseLocalFallback((prev) => (reloadTick >= 1 ? true : prev))
                                        try { typeof onReady === 'function' && onReady() } catch {}
                                        return
                                    }
                                    if (skipHeaders && authHeaders) {
                                        // Próximo intento: con headers
                                        setSkipHeaders(false)
                                        setReloadTick((x) => x + 1)
                                        try { typeof onReady === 'function' && onReady() } catch {}
                                        return
                                    }
                                    if (!skipHeaders) {
                                        // Próximo intento: sin headers
                                        setSkipHeaders(true)
                                        setReloadTick((x) => x + 1)
                                        try { typeof onReady === 'function' && onReady() } catch {}
                                        return
                                    }
                                    // Cambiar a RNImage como siguiente intento
                                    if (!useRNImageNative) {
                                        setUseRNImageNative(true)
                                        setReloadTick((x) => x + 1)
                                        try { typeof onReady === 'function' && onReady() } catch {}
                                        return
                                    }
                                    // Si llegamos aquí, usar placeholder local
                                    setUseLocalFallback(true)
                                    try { typeof onReady === 'function' && onReady() } catch {}
                                }}
                                onLoadStart={() => {
                                    // Evitar spam de logs: sólo marcar estado
                                    setStartedLoad(true)
                                }}
                                onLoadEnd={() => contentSeen && contentSeen()}
                            />
                            )}
                        </View>
                    </PinchZoomImage>
                ))
            )}
        </>
    )
})

/**
 * @description busca la imagen en local si existe, si no la encuentra devuelve la uri original
 * @param {string} uri link
 * @param {string} path path en el que se encuentra la imagen
 * @returns {Promise<string>}uri de la imagen
 */
export const searchFile = async (uri, path) => {
    // Path example :
    // => post => post
    // => courses => courses/${_idCourse}
    // => stores/portfolio => assets

    // En web evitamos buscar en FileSystem y devolvemos directamente la URI normalizada
    if (Platform.OS === 'web') {
        return sanitizeUri(uri)
    }

    const imageNameOriginal = uri.split('/').pop()
    let imageName = uri.split('/').pop() // nombre de la imagen + extension
    let extension = imageName.split('.').pop() // extension de la imagen

    if (!imageName || imageName === '') {
        // console.log('No se pudo obtener el nombre de la imagen')
        return uri
    }

    // Busco ese corte en FileSystem.cacheDirectory

    //Agregar path por parametro => ej: courses/${_id} courses/672cdf59de9b4c8a2a032289

    if (extension === 'png' || extension === 'jpg' || extension === 'jpeg') {
        const ext = '.' + extension
        imageName = imageName.replace(ext, '.webp')
    }

    const sanitizePath = (p) => {
        if (!p) return ''
        let s = p.trim()
        // quitar dobles barras y barras al inicio/fin
        s = s.replace(/\/+$/g, '').replace(/^\//g, '')
        return s
    }
    const normalizedPath = sanitizePath(path)
    const directoryUri = `${documentDirectory}unzipped/${normalizedPath}`

    const directoryPath = `${directoryUri}/${imageName}`

    try {
        console.log('[ImageWithZoom.searchFile] try local ->', directoryPath)
        const fileInfo = await getInfoAsync(directoryPath)

    const directoryPathOriginal = `${directoryUri}/${imageNameOriginal}`

        const fileInfoOriginal = await getInfoAsync(directoryPathOriginal)
        let finalImage = fileInfo.exists ? fileInfo.uri : fileInfoOriginal.exists ? fileInfoOriginal.uri : uri
        console.log('[ImageWithZoom.searchFile] exists(webp/original)?', fileInfo.exists, fileInfoOriginal.exists, '->', finalImage)
        return finalImage
    } catch (_e) {
        // En web u otros entornos sin FileSystem real, devolver la uri remota
        console.log('[ImageWithZoom.searchFile] getInfoAsync failed -> using remote', _e?.message)
        return uri
    }
}

export const ImageWithZoomStores = ({ uri }) => {
    const [image, setImage] = useState(uri ? uri : placeholderGeneral)
    return (
        <PinchZoomImage maxScale={3} minScale={1} enabled={Platform.OS !== 'web'}>
            <ExpoImage
                source={{ uri: image }}
                style={styles.imageWithZoomStore}
                onError={(e) => setImage(placeholderGeneral)}
                contentFit='cover'
                cachePolicy={'memory-disk'}
            />
        </PinchZoomImage>
    )
}
